import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';

export default function RobinhoodPositions() {
  const { privacyMask } = usePrivacyMask();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const fetchPositions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/mcp/positions');
      setPositions(response.data.positions || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  const formatCurrency = (value) => {
    if (privacyMask) return '••••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const handleRowClick = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Group positions by underlying symbol (for options)
  const groupPositions = (positions) => {
    const grouped = {};
    positions.forEach((pos, idx) => {
      const key = pos.isOption ? (pos.optionInfo?.underlying || pos.symbol) : pos.symbol;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push({ ...pos, originalIndex: idx });
    });
    return grouped;
  };

  const groupedPositions = groupPositions(positions);

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
          <Button
            variant="contained"
            onClick={fetchPositions}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && positions.length === 0 && (
          <Alert severity="info">
            No positions found
          </Alert>
        )}

        {!loading && positions.length > 0 && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Found {positions.length} position(s)
            </Alert>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Symbol</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Avg Price</TableCell>
                    <TableCell align="right">Current Price</TableCell>
                    <TableCell align="right">Market Value</TableCell>
                    <TableCell align="right">Cost Basis</TableCell>
                    <TableCell align="right">P/L</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map((position, index) => {
                    const pl = position.marketValue - position.cost_basis;
                    const isOption = position.isOption;
                    const optionInfo = position.optionInfo;

                    return (
                      <React.Fragment key={index}>
                        <TableRow 
                          hover
                          onClick={() => isOption && handleRowClick(index)}
                          sx={{ cursor: isOption ? 'pointer' : 'default' }}
                        >
                          <TableCell>
                            {isOption && (
                              <IconButton size="small">
                                {expandedRows[index] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            <strong>{position.symbol}</strong>
                            {isOption && optionInfo && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {optionInfo.underlying} {optionInfo.expiration} {optionInfo.type} {optionInfo.strike}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {isOption ? (
                              <Chip
                                label={optionInfo?.type === 'C' ? 'Call' : 'Put'}
                                size="small"
                                variant="outlined"
                                color="default"
                              />
                            ) : (
                              <Chip
                                label="Stock"
                                size="small"
                                variant="outlined"
                                color="default"
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(position.quantity)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.averagePrice)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.currentPrice)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.marketValue)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(position.cost_basis)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: pl >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                            {formatCurrency(pl)}
                          </TableCell>
                        </TableRow>
                        {isOption && expandedRows[index] && (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ pb: 0, pt: 0 }}>
                              <Collapse in={expandedRows[index]}>
                                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Option Details
                                  </Typography>
                                  <Typography variant="body2">
                                    Underlying: <strong>{optionInfo?.underlying || position.symbol}</strong><br />
                                    Expiration: <strong>{optionInfo?.expiration || position.expirationDate}</strong><br />
                                    Type: <strong>{optionInfo?.type === 'C' ? 'Call' : 'Put'}</strong><br />
                                    Strike: <strong>{formatCurrency(optionInfo?.strike || position.strike)}</strong><br />
                                    Original Symbol: <strong>{position.originalSymbol}</strong>
                                  </Typography>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Raw Data</Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.100', 
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 400
              }}>
                <pre>{JSON.stringify(positions, null, 2)}</pre>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
}
